import { AlertTriangle, CheckCircle2, Minus, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { exactTime, humanSize } from '@/modules/backups/format';
import { lastSuccessfulBackup, type Tone } from '@/modules/backups/health';
import { panel, toneText } from '@/components/surface/tones';
import { relativeTime } from '@/modules/monitoring/format';
import type { BackupOverview } from '@/types/api';

/**
 * Four figures of equal weight, before a single row is read.
 *
 * Each card answers one question in the same shape — what, how it stands, one line of context — so
 * none of them outranks the others by size. Every figure is one the page states in more detail
 * further down; this only puts them where they are seen first.
 */
export function BackupsSummary({ data }: { data: BackupOverview }) {
  const summary = data.verification.summary;

  const sources = data.sources.length;
  const protectedSources = Math.min(summary.sources_backed_up, sources);
  const unscheduled = data.sources.filter((source) => !data.schedules.some((schedule) => schedule.source_id === source.id)).length;

  const last = lastSuccessfulBackup(data.backups);
  const lastTest = summary.last_restore_test;
  const failing = data.verification.restore_tests.filter((test) => test.status === 'failed');

  const confirmed = summary.backups_restorable + summary.backups_stored;
  const succeeded = confirmed + summary.backups_unverified + summary.backups_failed_verification;
  const offsite = data.destinations.filter((destination) => destination.is_offsite);

  return (
    <section aria-label="Backup summary" className="grid grid-cols-2 gap-3 xl:grid-cols-4">
      <Metric
        label="Protected sources"
        tone={sources === 0 ? 'muted' : protectedSources === sources ? 'success' : 'warning'}
        value={String(protectedSources)}
        unit={sources === 0 ? undefined : `of ${sources}`}
        detail={
          sources === 0
            ? 'Nothing to back up yet'
            : `With a recent successful backup${unscheduled > 0 ? ` · ${unscheduled} without a schedule` : ''}`
        }
      />

      <Metric
        label="Last successful backup"
        tone={last !== null ? 'success' : data.backups.length > 0 ? 'danger' : 'muted'}
        value={last === null ? 'None yet' : relativeTime(last.finished_at ?? last.started_at)}
        valueTitle={last === null ? undefined : exactTime(last.finished_at ?? last.started_at)}
        detail={
          last !== null
            ? `${last.source_name} · ${humanSize(last.size_bytes)}`
            : data.backups.length > 0
              ? 'No recent backup succeeded'
              : 'No backups yet'
        }
      />

      <Metric
        label="Restore verification"
        tone={failing.length > 0 ? 'danger' : lastTest === null ? 'muted' : lastTest.status === 'passed' ? 'success' : 'muted'}
        value={
          failing.length > 0
            ? `${failing.length} failing`
            : lastTest === null
              ? 'Not tested yet'
              : lastTest.status === 'passed'
                ? 'Passed'
                : lastTest.status_label
        }
        valueTitle={lastTest === null ? undefined : `Latest restore test: ${exactTime(lastTest.started_at)}`}
        detail={
          failing.length > 0
            ? failing.length === 1
              ? `${failing[0].source_name} failed its latest restore test`
              : `${failing.map((test) => test.source_name).join(', ')} failed their latest restore test`
            : lastTest === null
              ? 'The first runs within a day of a backup'
              : `${lastTest.source_name} · ${relativeTime(lastTest.started_at)} · ${summary.sources_verified} of ${summary.sources_backed_up} tested this week`
        }
      />

      <Metric
        label="Storage protection"
        tone={
          data.destinations.length === 0
            ? 'muted'
            : summary.backups_failed_verification > 0
              ? 'danger'
              : offsite.length === 0
                ? 'warning'
                : 'success'
        }
        value={data.destinations.length === 0 ? 'No storage' : offsite.length > 0 ? 'Offsite' : 'Server only'}
        detail={
          data.destinations.length === 0
            ? 'Add a destination to take a backup'
            : summary.backups_failed_verification > 0
              ? `${summary.backups_failed_verification} backup${summary.backups_failed_verification === 1 ? '' : 's'} failed verification`
              : succeeded > 0
                ? `${confirmed} of ${succeeded} recent backups confirmed stored`
                : offsite.length > 0
                  ? offsite.map((destination) => destination.name).join(', ')
                  : 'Lost if the server is lost'
        }
      />
    </section>
  );
}

function Metric({
  label,
  tone,
  value,
  valueTitle,
  unit,
  detail,
}: {
  label: string;
  tone: Tone;
  value: string;
  valueTitle?: string | undefined;
  unit?: string | undefined;
  detail: string;
}) {
  const Icon = tone === 'success' ? CheckCircle2 : tone === 'danger' ? XCircle : tone === 'warning' ? AlertTriangle : Minus;

  return (
    <div className={cn(panel, 'flex min-w-0 flex-col p-4 sm:p-5')}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-medium leading-5 text-muted-foreground sm:text-[13px]">{label}</p>
        <Icon
          className={cn('mt-0.5 size-4 shrink-0', toneText[tone])}
          aria-label={tone === 'success' ? 'Good' : tone === 'danger' ? 'Problem' : tone === 'warning' ? 'Needs attention' : 'No data yet'}
        />
      </div>
      <p className="mt-2.5 flex min-w-0 items-baseline gap-1.5" title={valueTitle}>
        <span className="truncate text-xl font-semibold leading-7 tracking-tight tabular-nums sm:text-2xl sm:leading-8">{value}</span>
        {unit !== undefined && <span className="shrink-0 text-sm text-muted-foreground">{unit}</span>}
      </p>
      <p className="mt-1 line-clamp-2 text-[13px] leading-5 text-muted-foreground" title={detail}>
        {detail}
      </p>
    </div>
  );
}
